import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../components/ui/alert-dialog';
import { Toaster } from '../components/ui/sonner';
import { HarmonyLogoCompact } from '../components/HarmonyLogo';
import {
  RefreshCw, Check, CheckCircle2, Save, Send, CalendarDays, AlertTriangle, Target,
} from 'lucide-react';
import { formatDate } from '../components/growth/helpers';
import usePageMeta from '../hooks/usePageMeta';

// The public self-appraisal form, opened from an emailed link. It is the only
// Harmony Growth screen a non-HR colleague ever sees, so it deliberately does
// NOT use GrowthContext: there is no session, no Authorization header and no
// route back into the authenticated app.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Toasts go to this page's own top-center Toaster, not the app-wide top-right
// one in App.js. A Toaster with an id shows only the toasts sent with that
// toasterId, and the app-wide one (no id) skips them, so each shows once.
const TOASTER_ID = 'growth-self-appraisal';

// Draft is written back every 30s and on every blur. People abandon long forms
// when they are not sure their typing is safe, so the timer is short and the
// indicator is always visible in the action bar.
const AUTOSAVE_MS = 30000;

// The six questions, in the order they are stored on growth_self_appraisals.
// The helper line matters as much as the question — most people have never
// written one of these before and freeze without an example of "good".
const SECTIONS = [
  {
    key: 'accomplishments',
    label: 'What you accomplished this year',
    helper: 'The work you are proudest of. Two or three concrete examples say far more than a long list — a project you delivered, a problem you solved, something you built or fixed.',
    placeholder: 'For example: "Rebuilt the reporting module — cut the monthly close from three days to one."',
    rows: 6,
  },
  {
    key: 'impact',
    label: 'The difference it made',
    helper: 'What changed because you did that work — for your team, your project or the client. Numbers, timelines or a specific outcome are ideal, but plain description is fine too.',
    placeholder: 'Who was better off, and how?',
    rows: 5,
  },
  {
    key: 'challenges',
    label: 'Challenges and blockers',
    helper: 'What got in the way this year, and how you handled it. Be candid — this is a working conversation, not a test. Naming a blocker is the first step to removing it.',
    placeholder: 'Anything that slowed you down: unclear requirements, tooling, dependencies, workload.',
    rows: 5,
  },
  {
    key: 'growth',
    label: 'How you have grown',
    helper: 'Skills, tools or responsibilities you picked up — including the things you learned the hard way. Mistakes you learned from belong here.',
    placeholder: 'What can you do now that you could not do a year ago?',
    rows: 5,
  },
  {
    key: 'aspirations',
    label: 'Where you would like to go next',
    helper: 'The kind of work, skills or responsibility you would like more of over the next year. Say it plainly — nobody can act on a preference they do not know about.',
    placeholder: 'What would you like the next year to look like?',
    rows: 5,
  },
  {
    key: 'support_needed',
    label: 'Support you need',
    helper: 'What would help you do your best work: training, tools, clearer direction, a different kind of project, more or less of something.',
    placeholder: 'Be specific — specific asks are the ones that get actioned.',
    rows: 5,
  },
];

const EMPTY_FORM = {
  accomplishments: '',
  impact: '',
  challenges: '',
  growth: '',
  aspirations: '',
  support_needed: '',
  self_ratings: {},
};

// The public endpoint carries its own labels, but this form has no
// authenticated /competencies call to fall back on, so keep a copy of the
// shared scale here rather than rendering bare numbers if they are missing.
const FALLBACK_RATING_LABELS = {
  1: 'Below Expectations',
  2: 'Meets Expectations',
  3: 'Exceeds Expectations',
  4: 'Outstanding',
};

const NOT_OBSERVED = 'none';

const firstNameOf = (name) => String(name || '').trim().split(/\s+/)[0] || '';

const formatClock = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

// One-line, always-present reassurance. Deliberately quiet: it should be
// noticed when looked for and ignored otherwise.
const SaveIndicator = ({ state, savedAt }) => {
  if (state === 'saving') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-slate-500">
        <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Saving…
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span
        className="flex items-center gap-1.5 text-xs text-emerald-600"
        data-testid="growth-appraisal-saved"
      >
        <Check className="w-3.5 h-3.5" />
        Saved{savedAt ? ` at ${formatClock(savedAt)}` : ''}
      </span>
    );
  }
  if (state === 'error') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-amber-600">
        <AlertTriangle className="w-3.5 h-3.5" /> Not saved yet — still trying
      </span>
    );
  }
  if (state === 'dirty') {
    return <span className="text-xs text-slate-400">Unsaved changes</span>;
  }
  return <span className="text-xs text-slate-400">Your draft saves automatically</span>;
};

const CentredCard = ({ children }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
    <Card className="w-full max-w-md">
      <CardContent className="pt-8 pb-8 text-center space-y-3">{children}</CardContent>
    </Card>
  </div>
);

const GrowthSelfAppraisal = () => {
  usePageMeta('Self-Appraisal | Harmony Growth', '/favicon-growth.svg');

  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // 'invalid' | 'unavailable'
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [saveState, setSaveState] = useState('idle'); // idle|dirty|saving|saved|error
  const [savedAt, setSavedAt] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);

  // Refs so the 30s timer and the unload guard always see current values
  // without being torn down and rebuilt on every keystroke.
  const formRef = useRef(form);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const submittedRef = useRef(false);

  useEffect(() => { formRef.current = form; }, [form]);

  const url = useMemo(
    () => `${BACKEND_URL}/api/growth/public/appraisal/${encodeURIComponent(token || '')}`,
    [token],
  );

  const load = useCallback(async () => {
    if (!token) {
      setLoadError('invalid');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // No Authorization header, by design — the token in the path IS the auth.
      const res = await axios.get(url);
      const payload = res.data || {};
      const appraisal = payload.appraisal || {};

      setData(payload);
      setForm({
        accomplishments: appraisal.accomplishments || '',
        impact: appraisal.impact || '',
        challenges: appraisal.challenges || '',
        growth: appraisal.growth || '',
        aspirations: appraisal.aspirations || '',
        support_needed: appraisal.support_needed || '',
        self_ratings: appraisal.self_ratings || {},
      });
      dirtyRef.current = false;
      setSaveState(appraisal.updated_at ? 'saved' : 'idle');
      setSavedAt(appraisal.updated_at || null);

      if (appraisal.status === 'submitted') {
        submittedRef.current = true;
        setSubmitted(true);
        setSubmittedAt(appraisal.submitted_at || null);
      }
      setLoadError(null);
    } catch (err) {
      // A wrong, revoked or expired link is indistinguishable to the person
      // holding it — and must stay that way. Anything else is our problem, not
      // theirs, so offer a retry instead of a dead end.
      const status = err?.response?.status;
      setLoadError(status && status >= 400 && status < 500 ? 'invalid' : 'unavailable');
    } finally {
      setLoading(false);
    }
  }, [token, url]);

  useEffect(() => { load(); }, [load]);

  const persist = useCallback(async (action) => {
    const res = await axios.post(url, { action, ...formRef.current });
    return res.data || {};
  }, [url]);

  const saveDraft = useCallback(async ({ silent = true, force = false } = {}) => {
    if (submittedRef.current || savingRef.current) return;
    if (!force && !dirtyRef.current) return;

    // Identity snapshot: setForm always produces a new object, so if the user
    // typed while the request was in flight the draft is still dirty and the
    // next tick must pick it up.
    const snapshot = formRef.current;
    savingRef.current = true;
    setSaveState('saving');
    try {
      const res = await persist('save');
      if (formRef.current === snapshot) dirtyRef.current = false;
      setSavedAt(res.updated_at || new Date().toISOString());
      setSaveState(formRef.current === snapshot ? 'saved' : 'dirty');
      if (!silent) toast.success('Draft saved', { toasterId: TOASTER_ID });
    } catch (err) {
      setSaveState('error');
      if (!silent) {
        toast.error('Could not save your draft', {
          toasterId: TOASTER_ID,
          description: err?.response?.data?.detail || err.message,
        });
      }
    } finally {
      savingRef.current = false;
    }
  }, [persist]);

  // Autosave. Keeps ticking after a failure — dirtyRef stays true, so a dropped
  // connection recovers on its own without the person noticing.
  useEffect(() => {
    if (!data || submitted) return undefined;
    const id = setInterval(() => { saveDraft({ silent: true }); }, AUTOSAVE_MS);
    return () => clearInterval(id);
  }, [data, submitted, saveDraft]);

  // Last line of defence: never let someone close the tab on unsaved answers.
  useEffect(() => {
    const handler = (e) => {
      if (!dirtyRef.current || submittedRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const setField = (key, value) => {
    dirtyRef.current = true;
    setSaveState('dirty');
    setForm((f) => ({ ...f, [key]: value }));
  };

  const setRating = (key, value) => {
    dirtyRef.current = true;
    setSaveState('dirty');
    setForm((f) => ({ ...f, self_ratings: { ...f.self_ratings, [key]: value } }));
  };

  const onBlurSave = () => { saveDraft({ silent: true }); };

  const openConfirm = () => {
    if (!form.accomplishments.trim()) {
      return toast.error('Tell us what you accomplished first', {
        toasterId: TOASTER_ID,
        description: 'The first question is the one your reviewers rely on most.',
      });
    }
    return setConfirmOpen(true);
  };

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await persist('submit');
      dirtyRef.current = false;
      submittedRef.current = true;
      setSubmittedAt(res.submitted_at || new Date().toISOString());
      setSubmitted(true);
      setConfirmOpen(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error('Could not submit your appraisal', {
        toasterId: TOASTER_ID,
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resource = data?.resource || {};
  const cycle = data?.cycle || {};
  const displayName = resource.display_name || data?.resource_name || '';
  const firstName = firstNameOf(displayName);
  const competencies = data?.competencies || [];
  const ratingLabels = data?.rating_labels || FALLBACK_RATING_LABELS;
  const priorGoals = data?.prior_goals || [];

  const blankSections = SECTIONS
    .filter((s) => !String(form[s.key] || '').trim())
    .map((s) => s.label);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-slate-500">
          <RefreshCw className="w-6 h-6 animate-spin text-emerald-600" />
          <p className="text-sm">Loading your form…</p>
        </div>
      </div>
    );
  }

  if (loadError === 'invalid') {
    return (
      <CentredCard>
        <div className="flex justify-center"><HarmonyLogoCompact size={40} /></div>
        <h1 className="text-xl font-semibold text-slate-800">This link is no longer valid</h1>
        <p className="text-sm text-slate-600">
          Please contact HR and they will send you a new one. Nothing you have
          written before has been lost.
        </p>
      </CentredCard>
    );
  }

  if (loadError === 'unavailable') {
    return (
      <CentredCard>
        <div className="flex justify-center"><HarmonyLogoCompact size={40} /></div>
        <h1 className="text-xl font-semibold text-slate-800">We could not open your form</h1>
        <p className="text-sm text-slate-600">
          Something went wrong at our end. Please try again in a moment — your
          link still works.
        </p>
        <div className="pt-1">
          <Button onClick={load} data-testid="growth-appraisal-retry">
            <RefreshCw className="w-4 h-4 mr-2" /> Try again
          </Button>
        </div>
      </CentredCard>
    );
  }

  if (submitted) {
    return (
      <CentredCard>
        <div className="w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-7 h-7 text-emerald-600" />
        </div>
        <h1 className="text-xl font-semibold text-slate-800" data-testid="growth-appraisal-done">
          Thank you{firstName ? `, ${firstName}` : ''}
        </h1>
        <p className="text-sm text-slate-600">
          Your self-appraisal has been received. It goes to HR and to the people
          reviewing your year, and it will be part of your review conversation.
        </p>
        {submittedAt && (
          <p className="text-xs text-slate-500">
            Submitted {formatDate(submittedAt)}
            {formatClock(submittedAt) ? ` at ${formatClock(submittedAt)}` : ''}
          </p>
        )}
        <p className="text-xs text-slate-400 pt-2">
          You can close this page now. If something needs changing, contact HR.
        </p>
      </CentredCard>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="growth-self-appraisal">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <HarmonyLogoCompact size={32} />
          <div className="min-w-0">
            <p className="font-semibold text-slate-800 leading-tight">Your self-appraisal</p>
            <p className="text-xs text-slate-500">Harmony Growth</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        <Card>
          <CardContent className="pt-6">
            <h1 className="text-xl sm:text-2xl font-semibold text-slate-800">
              Hello{firstName ? ` ${firstName}` : ''}
            </h1>
            <p className="text-sm text-slate-600 mt-2">
              This is your chance to put your own view of the year on record, in
              your words, before your review conversation. There are six short
              questions and a self-rating section. Nothing here is scored — the
              more specific you are, the more useful the conversation will be.
            </p>

            <div className="mt-4 flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:gap-x-6 text-sm text-slate-600">
              {(cycle.period_start || cycle.period_end) && (
                <span className="flex items-center gap-1.5">
                  <CalendarDays className="w-4 h-4 text-slate-400" />
                  Review period: {formatDate(cycle.period_start)} – {formatDate(cycle.period_end)}
                </span>
              )}
              {cycle.due_on && (
                <span className="flex items-center gap-1.5">
                  <Check className="w-4 h-4 text-slate-400" />
                  Please complete by {formatDate(cycle.due_on)}
                </span>
              )}
            </div>

            <p className="text-xs text-slate-500 mt-3">
              Your answers save automatically as you go. You can close this page
              and come back later using the same link.
            </p>
          </CardContent>
        </Card>

        {priorGoals.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="w-4 h-4 text-slate-500" />
                What you agreed last time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-2">
                Worth a look before you start — it is fine if things changed.
              </p>
              <ul className="divide-y divide-slate-100">
                {priorGoals.map((g, i) => (
                  <li key={g.id || i} className="py-2 first:pt-0">
                    <p className="text-sm font-medium text-slate-800">{g.title}</p>
                    {g.detail && <p className="text-sm text-slate-600">{g.detail}</p>}
                    {g.target_date && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        Target {formatDate(g.target_date)}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {SECTIONS.map((s, idx) => (
          <Card key={s.key}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-start gap-2.5">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-xs font-semibold text-emerald-700">
                  {idx + 1}
                </span>
                <span>{s.label}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-slate-500">{s.helper}</p>
              <Label htmlFor={`growth-appraisal-${s.key}`} className="sr-only">
                {s.label}
              </Label>
              <Textarea
                id={`growth-appraisal-${s.key}`}
                data-testid={`growth-appraisal-${s.key}`}
                rows={s.rows}
                placeholder={s.placeholder}
                value={form[s.key]}
                onChange={(e) => setField(s.key, e.target.value)}
                onBlur={onBlurSave}
              />
            </CardContent>
          </Card>
        ))}

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-start gap-2.5">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-xs font-semibold text-emerald-700">
                7
              </span>
              <span>How you would rate yourself</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-500">
              This is a starting point for the conversation, not a verdict. Rate
              yourself honestly — most people land on “Meets Expectations” for
              most things, and that is a good place to be. Choose “Not observed”
              where the work simply did not come up this year.
            </p>

            {competencies.length === 0 ? (
              <p className="text-sm text-slate-500 py-4">
                There is nothing to rate on this form. Answer the questions above
                and submit when you are ready.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {competencies.map((c) => {
                  const value = form.self_ratings[c.key];
                  const anchor = value == null ? '' : (c.anchors || {})[String(value)];
                  return (
                    <li key={c.key} className="py-3 first:pt-0">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800">{c.name}</p>
                          {c.description && (
                            <p className="text-xs text-slate-500">{c.description}</p>
                          )}
                        </div>
                        <Select
                          value={value == null ? NOT_OBSERVED : String(value)}
                          onValueChange={(v) =>
                            setRating(c.key, v === NOT_OBSERVED ? null : Number(v))}
                        >
                          <SelectTrigger
                            className="w-full sm:w-[240px] h-9 shrink-0"
                            data-testid={`growth-appraisal-rating-${c.key}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NOT_OBSERVED}>Not observed</SelectItem>
                            {[1, 2, 3, 4].map((v) => (
                              <SelectItem key={v} value={String(v)}>
                                {ratingLabels[v] || FALLBACK_RATING_LABELS[v]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {anchor && (
                        <p className="text-xs text-slate-500 mt-1.5 italic">{anchor}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        <div className="rounded-md border border-slate-200 bg-white p-3">
          <p className="text-sm text-slate-600">
            Happy with your answers? Submitting sends them on. Once submitted,
            the form is locked and you will not be able to change it — so read it
            through first.
          </p>
        </div>
      </main>

      {/* Action bar stays in reach on a phone without covering the last field. */}
      <div className="sticky bottom-0 z-10 border-t border-slate-200 bg-white/95 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <SaveIndicator state={saveState} savedAt={savedAt} />
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => saveDraft({ silent: false, force: true })}
              disabled={saveState === 'saving'}
              data-testid="growth-appraisal-save-draft"
            >
              <Save className="w-4 h-4 mr-2" />
              Save draft
            </Button>
            <Button size="sm" onClick={openConfirm} data-testid="growth-appraisal-submit">
              <Send className="w-4 h-4 mr-2" />
              Submit
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit your self-appraisal?</AlertDialogTitle>
            <AlertDialogDescription>
              Once you submit, this form is locked and you will not be able to
              change your answers. If you would like more time, keep editing —
              your draft is saved and this link will still work.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {blankSections.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-medium mb-1">
                {blankSections.length === 1
                  ? 'One question is still blank:'
                  : `${blankSections.length} questions are still blank:`}
              </p>
              <p>{blankSections.join(' · ')}</p>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); onSubmit(); }}
              disabled={submitting}
              data-testid="growth-appraisal-confirm-submit"
            >
              {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
              Yes, submit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Toaster id={TOASTER_ID} position="top-center" richColors closeButton />
    </div>
  );
};

export default GrowthSelfAppraisal;
